import type { CSSProperties } from 'react';
import { UI_ICONS } from '@/assets/generated/ui';

/**
 * Line icon from the exported UI set (svgs/export/ui), 48×48 grid, currentColor.
 * Ids: ui_stat_str, ui_hp, ui_attack, ui_defense, ui_power, ui_energy, ui_xp, ui_scrap, ui_premium,
 * ui_stance_{peaceful,cautious,aggressive,shadow}, ui_event_{encounter,combat,ambush,trade,escape,loot,follow,rumor,discovery},
 * ui_danger, ui_open_pvp, ui_nav_{camp,map,survivor,inventory,feed},
 * ui_action_{equip,dismantle,repair,craft,share,revenge}, ui_state_{locked,new,timer,ghost}.
 */
export function Icon({ id, size = 20, className, style, strokeWidth }: { id: string; size?: number; className?: string; style?: CSSProperties; strokeWidth?: number }) {
  const icon = UI_ICONS[id] ?? UI_ICONS[id.startsWith('ui_') ? id : `ui_${id}`];
  if (!icon) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox={icon.viewBox}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth ?? Math.max(1.5, (1.5 * 48) / size)}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden
      dangerouslySetInnerHTML={{ __html: icon.inner }}
    />
  );
}

/**
 * Small line icon. `d` is either an exported UI icon id (`ui_…`, rendered from
 * svgs/export/ui) or a 24×24 path for utility glyphs that have no exported
 * counterpart (chevrons, close, check, plus/minus) and for content-specific
 * pictograms (zone pins, skills, backgrounds).
 */
export function PathIcon({ d, size = 20, className, strokeWidth = 1.5, fill = 'none', style }: { d: string; size?: number; className?: string; strokeWidth?: number; fill?: string; style?: CSSProperties }) {
  if (d.startsWith('ui_')) {
    // 24-grid stroke → 48-grid units, never thinner than the brief's 1.5px line.
    return <Icon id={d} size={size} className={className} style={style} strokeWidth={Math.max(strokeWidth * 2, (1.5 * 48) / size)} />;
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} style={style} aria-hidden>
      <path d={d} />
    </svg>
  );
}

/**
 * Icon vocabulary. Game concepts point at the exported UI icon set
 * (svgs/export/ui); only small utility glyphs without an exported
 * counterpart stay as 24×24 paths.
 */
export const P = {
  // Exported icons (svgs/export/ui)
  camp: 'ui_nav_camp',
  map: 'ui_nav_map',
  survivor: 'ui_nav_survivor',
  inventory: 'ui_nav_inventory',
  feed: 'ui_nav_feed',
  energy: 'ui_energy',
  clock: 'ui_state_timer',
  lock: 'ui_state_locked',
  eye: 'ui_event_encounter',
  moon: 'ui_stance_shadow',
  warn: 'ui_danger',
  diamond: 'ui_event_loot',
  share: 'ui_action_share',
  target: 'ui_action_revenge',
  wrench: 'ui_action_craft',
  shield: 'ui_defense',
  horde: 'ui_event_combat',
  skills: 'ui_xp',
  xp: 'ui_xp',
  hp: 'ui_hp',
  attack: 'ui_attack',
  defense: 'ui_defense',
  power: 'ui_power',
  scrap: 'ui_scrap',
  premium: 'ui_premium',
  ghost: 'ui_state_ghost',
  newItem: 'ui_state_new',
  openPvp: 'ui_open_pvp',
  equip: 'ui_action_equip',
  dismantle: 'ui_action_dismantle',
  repair: 'ui_action_repair',
  craft: 'ui_action_craft',
  revenge: 'ui_action_revenge',
  rumor: 'ui_event_rumor',
  discovery: 'ui_event_discovery',
  // Utility glyphs (no exported counterpart)
  up: 'M12 19 V5 M6 11 L12 5 L18 11',
  play: 'M7 4 L20 12 L7 20 Z',
  check: 'M5 12 L10 17 L19 7',
  x: 'M6 6 L18 18 M18 6 L6 18',
  chevronDown: 'M6 9 L12 15 L18 9',
  chevronRight: 'M9 6 L15 12 L9 18',
  chevronLeft: 'M15 6 L9 12 L15 18',
  plus: 'M12 5 V19 M5 12 H19',
  /** 20. 10. 2026 (owner): a pencil, change the survivor's name. */
  pencil: 'M4 20 L4.6 16.2 L15.8 5 A2 2 0 0 1 18.6 5 L19 5.4 A2 2 0 0 1 19 8.2 L7.8 19.4 Z M14 6.8 L17.2 10',
  /** Coat hanger: the Appearance screen (docs/OUTFITI_garderoba.md, 4.1). */
  hanger: 'M9.8 6.2 A2.2 2.2 0 1 1 12 8.4 V9.6 M12 9.6 L3.4 15.6 C2.7 16.1 3 17.2 3.9 17.2 H20.1 C21 17.2 21.3 16.1 20.6 15.6 Z',
  minus: 'M5 12 H19',
  bell: 'M6 16 V11 A6 6 0 0 1 18 11 V16 L20 18 H4 Z M10 21 H14',
  gear: 'M12 9 A3 3 0 1 1 11.99 9 Z M12 2 V5 M12 19 V22 M2 12 H5 M19 12 H22 M4.9 4.9 L7 7 M17 17 L19.1 19.1 M4.9 19.1 L7 17 M17 7 L19.1 4.9',
  users: 'M9 4 A3.5 3.5 0 1 1 8.99 4 Z M2 20 C2 16 5 14 9 14 C13 14 16 16 16 20 M16 4.5 A3 3 0 0 1 16 10.5 M18 14 C20.5 14.5 22 16.5 22 20',
  /** 20. 10. 2026 (owner): the Survivors list (a person and a list) and Follow (a star). */
  roster: 'M7 4 A3 3 0 1 1 6.99 4 Z M2 19 C2 15.5 4 13.5 7 13.5 C10 13.5 12 15.5 12 19 M15 7 H22 M15 12 H22 M15 17 H20',
  star: 'M12 3 L14.6 8.6 L20.6 9.3 L16.1 13.4 L17.3 19.4 L12 16.4 L6.7 19.4 L7.9 13.4 L3.4 9.3 L9.4 8.6 Z',
  book: 'M5 5 A2 2 0 0 1 7 3 H19 V17 H7 A2 2 0 0 0 5 19 Z M5 19 A2 2 0 0 0 7 21 H19 V17 M9 7 H15',
  history: 'M4 12 A8 8 0 1 0 6.3 6.3 M4 4 V8 H8 M12 8 V12 L15 14',
  menu: 'M4 7 H20 M4 12 H20 M4 17 H20',
  /** Krug 5 (K15.2): the season path. */
  flag: 'M6 21 V4 M6 4 H17 L14.5 8 L17 12 H6',
  /** The Guide (29. 9. 2026): a question mark in a circle. */
  help: 'M12 3 A9 9 0 1 1 11.99 3 Z M9.5 9.5 A2.5 2.5 0 1 1 13 11.8 C12.3 12.2 12 12.8 12 13.6 V14 M12 17 V17.2',
  message: 'M4 5 H20 V16 H10 L6 20 V16 H4 Z M8 9 H16 M8 12 H13',
  /** 11. 10. 2026 (owner): the Market, a stall with an awning. */
  market: 'M3 9 L5 4 H19 L21 9 Z M3 9 V10.5 A3 3 0 0 0 9 10.5 A3 3 0 0 0 15 10.5 A3 3 0 0 0 21 10.5 V9 M5 13 V20 H19 V13 M10 20 V15 H14 V20',
};
